import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { Panel, ViewportPortal, useReactFlow, useStore } from '@xyflow/react';
import { useGT } from 'gt-react';
import type { ZipTie } from '@erp/workflows';
import {
  BAR_HALF_WIDTH,
  ZIP_TIE_GREY,
  ZIP_TIE_PALETTE,
  tieHalfLength,
  type EdgeGeometry,
  type Point,
} from 'src/lib/workflows/zipTieGeometry';
import { installZipTieGestures } from './installZipTieGestures';
import type { ZipTieAction } from '../hooks/useWorkflowZipTies';

const OVERLAY_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  overflow: 'visible',
  pointerEvents: 'none',
  zIndex: 5,
};

type StrokeLine = {
  start: Point;
  end: Point;
  cutting: boolean;
  tying: boolean;
};

function strapColor(tie: ZipTie): string {
  return tie.color ?? ZIP_TIE_GREY;
}

function strapStroke(cutting: boolean, active: boolean): string {
  if (cutting) return '#dc2626';
  if (active) return '#ffffff';
  return '#0f172a';
}

function strapWidth(cutting: boolean, active: boolean, zoom: number): number {
  return (cutting || active ? 1.5 : 1) / zoom;
}

function strokePaint(stroke: StrokeLine): string {
  if (stroke.cutting) return '#dc2626';
  if (stroke.tying) return '#2563eb';
  return '#94a3b8';
}

function ZipTieStroke({
  stroke,
  zoom,
}: {
  stroke: StrokeLine | null;
  zoom: number;
}) {
  if (!stroke) return null;
  const solid = stroke.cutting || stroke.tying;
  return (
    <line
      x1={stroke.start.x}
      y1={stroke.start.y}
      x2={stroke.end.x}
      y2={stroke.end.y}
      stroke={strokePaint(stroke)}
      strokeWidth={2 / zoom}
      strokeLinecap="round"
      strokeDasharray={solid ? undefined : `${6 / zoom} ${5 / zoom}`}
    />
  );
}

function ZipTieStrap({
  tie,
  zoom,
  cutting,
  active,
}: {
  tie: ZipTie;
  zoom: number;
  cutting: boolean;
  active: boolean;
}) {
  const gt = useGT();
  const half = tieHalfLength(tie);
  const stroke = strapStroke(cutting, active);
  const width = strapWidth(cutting, active, zoom);
  const degrees = (tie.angle * 180) / Math.PI;
  return (
    <g
      data-testid="zip-tie"
      transform={`translate(${tie.position.x} ${tie.position.y}) rotate(${degrees})`}
    >
      <title>
        {gt('Zip tie', {
          context: 'workflow canvas cable tie bundling connections',
        })}
      </title>
      <rect
        x={-BAR_HALF_WIDTH}
        y={-half}
        width={BAR_HALF_WIDTH * 2}
        height={half * 2}
        rx={BAR_HALF_WIDTH}
        fill={strapColor(tie)}
        stroke={stroke}
        strokeWidth={width}
      />
    </g>
  );
}

function ZipTieHint({
  readOnly,
  tieCount,
  edgeCount,
}: {
  readOnly: boolean;
  tieCount: number;
  edgeCount: number;
}) {
  const gt = useGT();
  if (readOnly || tieCount > 0 || edgeCount < 2) return null;
  return (
    <Panel position="top-center" className="pointer-events-none">
      <p className="rounded-md border border-slate-200 bg-white/90 px-2 py-1 text-xs text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-900/90 dark:text-slate-300">
        {gt('Hold Z and drag across connections to zip-tie them', {
          context: 'workflow editor hint for bundling edges',
        })}
      </p>
    </Panel>
  );
}

/** Recolour and remove items for a zip tie, shown in the canvas context menu. */
export function ZipTieMenuItems({
  onRecolor,
  onRemove,
}: {
  onRecolor: (color: string | undefined) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  const swatchLabel = {
    Red: gt('Red', { context: 'zip tie color swatch' }),
    Orange: gt('Orange', { context: 'zip tie color swatch' }),
    Yellow: gt('Yellow', { context: 'zip tie color swatch' }),
    Green: gt('Green', { context: 'zip tie color swatch' }),
    Teal: gt('Teal', { context: 'zip tie color swatch' }),
    Blue: gt('Blue', { context: 'zip tie color swatch' }),
    Purple: gt('Purple', { context: 'zip tie color swatch' }),
    Pink: gt('Pink', { context: 'zip tie color swatch' }),
    White: gt('White', { context: 'zip tie color swatch' }),
    Black: gt('Black', { context: 'zip tie color swatch' }),
  };
  return (
    <>
      <div className="grid grid-cols-5 gap-1 p-1">
        {ZIP_TIE_PALETTE.map((swatch) => (
          <button
            key={swatch.color}
            type="button"
            role="menuitem"
            className="h-5 w-5 rounded-sm border border-black/20"
            style={{ background: swatch.color }}
            aria-label={swatchLabel[swatch.name]}
            onClick={() => onRecolor(swatch.color)}
          />
        ))}
      </div>
      <button
        type="button"
        role="menuitem"
        className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
        onClick={() => onRecolor(undefined)}
      >
        {gt('Gray', { context: 'zip tie color, neutral strap' })}
      </button>
      <button
        type="button"
        role="menuitem"
        className="w-full rounded-sm px-2 py-1.5 text-left text-sm text-destructive hover:bg-accent"
        onClick={onRemove}
      >
        {gt('Remove zip tie', {
          context: 'workflow canvas, unbundle connections',
        })}
      </button>
    </>
  );
}

export function ZipTieOverlay({
  ties,
  edgeCount,
  readOnly,
  canvasRef,
  endpoints,
  previewCutTieIds,
  hoveredTieId,
  onHover,
  onPreview,
  onGesture,
  onCommit,
  onMenu,
}: {
  ties: readonly ZipTie[];
  edgeCount: number;
  readOnly: boolean;
  canvasRef: { readonly current: HTMLElement | null };
  endpoints: { readonly current: Map<string, EdgeGeometry> };
  previewCutTieIds: ReadonlySet<string>;
  hoveredTieId: string | null;
  onHover: (id: string | null) => void;
  onPreview: (edgeIds: string[], cutTieIds: string[]) => void;
  onGesture: (ties: ZipTie[] | null) => void;
  onCommit: (ties: ZipTie[], action: ZipTieAction) => void;
  /** Right-click on a strap, at screen coordinates. */
  onMenu: (tieId: string, client: Point) => void;
}) {
  const { screenToFlowPosition } = useReactFlow();
  const zoom = useStore((state) => state.transform[2]);
  const [stroke, setStroke] = useState<StrokeLine | null>(null);
  const latest = useRef({
    ties,
    readOnly,
    zoom,
    screenToFlowPosition,
    onHover,
    onPreview,
    onGesture,
    onCommit,
    onMenu,
  });
  // The window listeners read the latest props; refresh them after each commit.
  useLayoutEffect(() => {
    latest.current = {
      ties,
      readOnly,
      zoom,
      screenToFlowPosition,
      onHover,
      onPreview,
      onGesture,
      onCommit,
      onMenu,
    };
  });

  useEffect(() => {
    return installZipTieGestures({
      canvas: () => canvasRef.current,
      readOnly: () => latest.current.readOnly,
      zoom: () => latest.current.zoom,
      toFlow: (client) => latest.current.screenToFlowPosition(client),
      ties: () => latest.current.ties,
      edges: () => [...(endpoints.current?.values() ?? [])],
      hover: (id) => latest.current.onHover(id),
      preview: (edgeIds, cutTieIds) =>
        latest.current.onPreview(edgeIds, cutTieIds),
      gesture: (next) => latest.current.onGesture(next),
      commit: (next, action) => latest.current.onCommit(next, action),
      menu: (tie, client) => latest.current.onMenu(tie.id, client),
      stroke: setStroke,
    });
  }, [canvasRef, endpoints]);

  return (
    <>
      <ViewportPortal>
        <svg className="overflow-visible" style={OVERLAY_STYLE}>
          <ZipTieStroke stroke={stroke} zoom={zoom} />
          {ties.map((tie) => (
            <ZipTieStrap
              key={tie.id}
              tie={tie}
              zoom={zoom}
              cutting={previewCutTieIds.has(tie.id)}
              active={tie.id === hoveredTieId}
            />
          ))}
        </svg>
      </ViewportPortal>
      <ZipTieHint
        readOnly={readOnly}
        tieCount={ties.length}
        edgeCount={edgeCount}
      />
    </>
  );
}
